import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Clock, Gavel, MessageSquare, Minus, Plus, TrendingUp, X, CheckCircle2, Info, AlertCircle, AlertTriangle, ShieldCheck, Download, FileText, Zap, RefreshCw } from "lucide-react";
import { ImageGallery, SpecGrid } from "@forhandlerborsen/ui";
import { useAuctions } from "../../contexts/AuctionsContext";
import { BidAuctionCard } from "../../components/auction/BidAuctionCard";
import { formatCurrency, formatNumericInput, parseNumericInput } from "../../lib/auction-utils";
import { LiveTimer } from "../../components/auction/LiveTimer";
import { InvoicesModal } from "../../components/auction/InvoicesModal";
import type { AuctionSnapshotEvent } from "../../lib/auction-events";
import { usePlatformAuth } from "../../lib/use-platform-auth";
// calculateSpread import removed - handled by API

// ── Types ────────────────────────────────────────────────────────────────────

type BidHistoryEntry = {
  bidId: string;
  bidderOrganizationId: string;
  bidderName?: string | null;
  bidderNumber: number;
  amount: number;
  createdAt: string;
  isAutoBid: boolean;
  sellerPayout: number;
};

type AuctionOffer = {
  id: string;
  round: number;
  offeredBy: string;
  amount: number;
  notes: string | null;
  status: string; // "pending" | "accepted" | "rejected"
  createdAt: string;
  sellerPayout: number;
  buyerPrice: number;
};

type AuctionDetail = {
  id: string;
  referenceNumber: string;
  title: string;
  description: string | null;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
  type: "regular" | "flash";
  durationMinutes: number | null;
  currentBid: number;
  bidCount: number;
  startingBid: number;
  minimumIncrement: number;
  reservePrice: number | null;
  buyNowPrice: number | null;
  antiSnipingWindowSeconds: number;
  extendedCount: number;
  images: { url: string; thumbnailUrl: string | null; altText: string | null }[];
  equipment: any;
  vehicle: {
    id: string;
    make: string;
    model: string;
    variant: string | null;
    modelYear: number | null;
    mileage: number | null;
    registrationNumber: string | null;
    fuelType: string | null;
    transmission: string | null;
    bodyType: string | null;
    exteriorColor: string | null;
    interiorColor: string | null;
    wheelDrive: string | null;
    nextEuControl: string | null;
    conditionGrade: string | null;
    conditionSummary: string | null;
    technicalData: any;
    conditionReport?: { url: string } | null;
  } | null;
  primaryImage: { url: string; thumbnailUrl: string | null; altText: string | null } | null;
  sellerOrganizationId: string;
  highestBidderOrganizationId: string | null;
  myAutoBid: { id: string; maxBidAmount: number } | null;
  bidHistory: BidHistoryEntry[];
  offers: AuctionOffer[];
  invoices: Array<{ id: string; documentNumber: string | null; documentType: string }>;
  sellerPayout: number;
  buyerPrice: number;
  pickupPostalCode: string | null;
  pickupCity: string | null;
};

// ── Components ────────────────────────────────────────────────────────────────

function StatusBanner({ 
  auction, 
  isSeller, 
  organizationSlug,
  onRefresh
}: { 
  auction: AuctionDetail; 
  isSeller: boolean; 
  organizationSlug: string;
  onRefresh?: () => void;
}) {
  const { organizations } = usePlatformAuth();
  const currentOrg = organizations.find((o) => o.slug === organizationSlug);
  const isWinner = !isSeller && auction.highestBidderOrganizationId === currentOrg?.id && auction.currentBid > 0;
  
  const getBannerConfig = () => {
    if (isSeller) {
      switch (auction.status) {
        case "pending_review":
          return { icon: Clock, label: "Sendt til gjennomgang – vi behandler din auksjon", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "changes_requested":
          return { icon: AlertCircle, label: "Endringer ønsket – se tilbakemeldingen og oppdater annonsen", color: "bg-orange-50 text-orange-800 border-orange-200" };
        case "approved":
          return { icon: CheckCircle2, label: "Godkjent – klar til publisering", color: "bg-green-50 text-green-800 border-green-200" };
        case "scheduled":
          return { icon: Clock, label: "Planlagt – starter automatisk på angitt tidspunkt", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "pending_seller_decision":
          return { icon: Gavel, label: "Auksjonen er over. Din beslutning kreves – aksepter eller avslå høyeste bud.", color: "bg-primary text-white border-primary" };
        case "negotiating":
          return { icon: RefreshCw, label: "Forhandling pågår – se detaljer for å svare på motbud.", color: "bg-accent text-white border-accent" };
        case "sold":
          return { icon: ShieldCheck, label: "Gratulerer! Budet er godtatt. Vi klargjør nå kontrakten til kjøper.", color: "bg-green-50 text-green-800 border-green-200" };
        case "contract_pending":
          return { icon: FileText, label: "Kontrakt er sendt til kjøper og avventer signering.", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "invoice_pending":
          return { icon: Info, label: "Kontrakt er signert. Faktura klargjøres til kjøper.", color: "bg-green-50 text-green-800 border-green-200" };
        case "payment_pending":
          return { icon: Clock, label: "Faktura er sendt til kjøper – avventer betaling.", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "ready_for_pickup":
          return { icon: CheckCircle2, label: "Bilen er betalt! Den er nå klar for henting av kjøper.", color: "bg-green-600 text-white border-green-700" };
        case "picked_up":
          return { icon: CheckCircle2, label: "Transportør har hentet kjøretøyet.", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "in_transit":
          return { icon: Clock, label: "Kjøretøyet er under transport til kjøper.", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "delivered":
          return { icon: CheckCircle2, label: "Kjøretøyet er levert til kjøper.", color: "bg-green-50 text-green-800 border-green-200" };
        case "paid_out":
          return { icon: ShieldCheck, label: "Oppgjør fullført – beløpet er utbetalt til din konto.", color: "bg-green-50 text-green-800 border-green-200" };
        default:
          return null;
      }
    } else if (isWinner) {
      switch (auction.status) {
        case "pending_seller_decision":
          return { icon: Clock, label: "Auksjonen er over og du har høyeste bud! Avventer selgers beslutning.", color: "bg-primary text-white border-primary" };
        case "negotiating":
          return { icon: RefreshCw, label: "Forhandling pågår – se detaljer for å svare på motbud.", color: "bg-accent text-white border-accent" };
        case "sold":
          return { icon: ShieldCheck, label: "GRATULERER! Selger har godkjent budet ditt. Vi klargjør nå kjøpskontrakten.", color: "bg-green-50 text-green-800 border-green-200" };
        case "contract_pending":
          return { icon: FileText, label: "KONTRAKT KLAR: Vennligst se over og signer kontrakten under.", color: "bg-primary text-white border-primary shadow-lg ring-2 ring-primary/20" };
        case "invoice_pending":
          return { icon: Info, label: "Kontrakt signert! Vi klargjør nå din faktura.", color: "bg-green-50 text-green-800 border-green-200" };
        case "payment_pending":
          return { icon: Info, label: "FAKTURA KLAR: Faktura er sendt. Vennligst betal for å klargjøre bilen for henting.", color: "bg-accent text-white border-accent shadow-lg" };
        case "ready_for_pickup":
          return { icon: CheckCircle2, label: "BETALT: Bilen er nå klar for henting. Gratulerer med ny bil!", color: "bg-green-600 text-white border-green-700" };
        case "picked_up":
          return { icon: CheckCircle2, label: "Bilen er hentet fra selger og klargjøres for transport.", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "in_transit":
          return { icon: Clock, label: "Bilen er på vei til deg!", color: "bg-blue-50 text-blue-800 border-blue-200" };
        case "delivered":
          return { icon: CheckCircle2, label: "Bilen er levert! Bekreft mottak eller meld fra om avvik.", color: "bg-green-600 text-white border-green-700 shadow-lg" };
        case "no_sale":
          return { icon: X, label: "Auksjonen ble avsluttet uten salg.", color: "bg-red-50 text-red-800 border-red-200" };
        default:
          return null;
      }
    } else if (!isSeller && auction.highestBidderOrganizationId && ["sold", "no_sale", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered"].includes(auction.status)) {
      // Banners for buyers who didn't win
      switch (auction.status) {
        case "sold":
          return { icon: Info, label: "Auksjonen er over. Kjøretøyet er solgt til en annen budgiver.", color: "bg-muted text-muted-foreground border-border" };
        case "no_sale":
          return { icon: Info, label: "Auksjonen ble avsluttet uten at minstepris ble nådd.", color: "bg-muted text-muted-foreground border-border" };
        default:
          return { icon: Info, label: "Auksjonen er avsluttet.", color: "bg-muted text-muted-foreground border-border" };
      }
    }

    if (auction.type === "flash") {
      return { 
        icon: Zap, 
        label: "Lynauksjon – rask budrunde!", 
        color: "bg-accent text-white border-accent shadow-lg shadow-accent/20" 
      };
    }

    return null;
  };

  const config = getBannerConfig();
  if (!config) return null;

  const Icon = config.icon;

  const showContractBtn = isWinner && ["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"].includes(auction.status);
  const showInvoiceBtn = auction.invoices.length > 0;

  return (
    <div className={`mb-8 flex items-center gap-4 rounded-2xl border p-5 shadow-sm ${config.color}`}>
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm`}>
        <Icon className={`h-6 w-6 ${auction.type === "flash" ? "fill-current" : ""}`} />
      </div>
      <div>
        <p className="text-sm font-black leading-snug uppercase tracking-wide" style={{ fontFamily: "var(--font-display)" }}>
          {config.label}
        </p>
      </div>
    </div>
  );
}

function applyAuctionSnapshotToDetail(
  auction: AuctionDetail,
  event: AuctionSnapshotEvent,
): AuctionDetail {
  if (auction.id !== event.auctionId) {
    return auction;
  }

  // Apply public fields from SSE. User-specific data (bidHistory with org IDs,
  // sellerPayout, etc.) is refreshed via authenticated re-fetch.
  return {
    ...auction,
    currentBid: event.currentBid,
    bidCount: event.bidCount,
    endsAt: event.endsAt,
    extendedCount: event.extendedCount,
    minimumIncrement: event.minimumIncrement,
    status: event.status ?? auction.status,
    highestBidderOrganizationId: event.highestBidderOrganizationId ?? auction.highestBidderOrganizationId,
  };
}

// ── Bid Panel ─────────────────────────────────────────────────────────────────

function BidPanel({
  auction,
  organizationSlug,
  onBidPlaced,
  myHighestBid,
  onRefresh,
  setAuction,
}: {
  auction: AuctionDetail;
  organizationSlug: string;
  onBidPlaced: (event: AuctionSnapshotEvent) => void;
  myHighestBid: number | null;
  onRefresh?: () => void;
  setAuction?: React.Dispatch<React.SetStateAction<AuctionDetail | null>>;
}) {
  const isLive = auction.status === "live";
  const suggested = (auction.currentBid || auction.startingBid) + auction.minimumIncrement;
  const [amount, setAmount] = useState(suggested);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAutoBid, setShowAutoBid] = useState(false);
  const [maxBid, setMaxBid] = useState(auction.myAutoBid?.maxBidAmount ?? suggested);

  // Sync maxBid if myAutoBid changes
  useEffect(() => {
    if (auction.myAutoBid) {
      setMaxBid(auction.myAutoBid.maxBidAmount);
    }
  }, [auction.myAutoBid]);

  const { organizations } = usePlatformAuth();
  const currentOrg = organizations.find((o) => o.slug === organizationSlug);

  const hasBid = currentOrg ? auction.bidHistory.some((b) => b.bidderOrganizationId === currentOrg.id) || auction.myAutoBid !== null : false;
  const isOutbid = hasBid && auction.highestBidderOrganizationId !== null && auction.highestBidderOrganizationId !== currentOrg?.id;
  const isWinning = auction.highestBidderOrganizationId !== null && auction.highestBidderOrganizationId === currentOrg?.id && auction.currentBid > 0;
  const isSeller = currentOrg?.id === auction.sellerOrganizationId;

  // Animation for bid change
  const prevBidRef = useRef(auction.currentBid);
  const [highlightBid, setHighlightBid] = useState(false);

  useEffect(() => {
    if (auction.currentBid > prevBidRef.current) {
      setHighlightBid(true);
      const timer = setTimeout(() => setHighlightBid(false), 1000);
      prevBidRef.current = auction.currentBid;
      return () => clearTimeout(timer);
    }
    prevBidRef.current = auction.currentBid;
  }, [auction.currentBid]);

  useEffect(() => {
    const next = (auction.currentBid || auction.startingBid) + auction.minimumIncrement;
    setAmount(next);
  }, [auction.currentBid, auction.startingBid, auction.minimumIncrement]);

  async function placeBid(bidAmount: number, maxBidAmount?: number) {
    setSubmitting(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/auctions/${auction.id}/bids`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: bidAmount,
          maxBidAmount: maxBidAmount ?? null,
          organizationSlug,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Budet ble ikke godtatt");
      } else {
        if (maxBidAmount && setAuction) {
          setAuction((prev: AuctionDetail | null) => (prev ? { ...prev, myAutoBid: { id: "temp", maxBidAmount } } : prev));
        }
        onBidPlaced(data.event as AuctionSnapshotEvent);
        setShowAutoBid(false);
      }
    } catch {
      setError("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  }

  async function updateAutoBid(newMax: number) {
    setSubmitting(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/auctions/${auction.id}/auto-bid`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          maxBidAmount: newMax,
          organizationSlug,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Kunne ikke oppdatere autobud");
      } else {
        if (setAuction) {
          setAuction((prev: AuctionDetail | null) => (prev ? { ...prev, myAutoBid: { id: "temp", maxBidAmount: newMax } } : prev));
        }
        onRefresh?.();
      }
    } catch {
      setError("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelAutoBid() {
    setSubmitting(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/auctions/${auction.id}/auto-bid`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organizationSlug }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Kunne ikke fjerne autobud");
      } else {
        if (setAuction) {
          setAuction((prev: AuctionDetail | null) => (prev ? { ...prev, myAutoBid: null } : prev));
        }
        onRefresh?.();
      }
    } catch {
      setError("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  }

  const quickIncrements = [
    auction.minimumIncrement,
    auction.minimumIncrement * 5,
    auction.minimumIncrement * 10,
  ];

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-lg border border-border">
      {/* Status bar */}
      {((isOutbid || isWinning || auction.myAutoBid) && !isSeller) && (
        <div
          className="flex items-center justify-between px-4 py-2.5 text-xs font-black uppercase tracking-widest text-white backdrop-blur-md"
          style={{ background: isOutbid ? "var(--accent)" : "#16a34a" }}
        >
          <span>
            {(() => {
              if (isOutbid) return "Overbudt";
              if (isLive) return "Leder budrunden";
              
              const statusMessages: Record<string, string> = {
                pending_seller_decision: "Budet vurderes",
                sold: "Du vant!",
                contract_pending: "Signer kontrakt",
                invoice_pending: "Kontrakt signert",
                payment_pending: "Faktura sendt",
                ready_for_pickup: "Betalt – klar for henting",
                picked_up: "Hentet av transport",
                in_transit: "Under transport",
                delivered: "Levert",
              };
              return statusMessages[auction.status] || "Du har høyeste bud";
            })()}
          </span>
          {auction.myAutoBid && isLive && (
            <div className="flex items-center gap-1.5 opacity-90">
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
              <span>Autobud aktiv</span>
            </div>
          )}
        </div>
      )}

      <div className="p-6">
        {/* Current bid + end time */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
              {(() => {
                const preLive = ["draft", "pending_review", "changes_requested", "approved", "scheduled"].includes(auction.status);
                if (preLive) return "Startpris";
                
                const isLive = auction.status === "live" || auction.status === "paused";
                if (isLive && auction.bidCount > 0) return "Gjeldende bud";

                if (isSeller && auction.bidCount > 0) return "Utbetaling";
                if (auction.bidCount > 0) return "Vinnende bud";
                return "Startpris";
              })()}
            </p>
            <div className="flex items-center gap-2 text-accent">
              <TrendingUp className="h-3.5 w-3.5" />
              <span className="text-[11px] font-black uppercase tracking-widest">{auction.bidCount} bud</span>
            </div>
          </div>
          <div className="flex flex-col">
            <p
              className={`text-4xl font-black transition-all ${highlightBid ? "animate-bid-pop text-green-600" : "text-primary"}`}
              style={{ fontFamily: "var(--font-display)" }}
            >
              {formatCurrency(
                isSeller && auction.bidCount > 0 ? auction.sellerPayout : (auction.bidCount > 0 ? auction.currentBid : auction.startingBid)
              )}
            </p>
          </div>

          <LiveTimer endsAt={auction.endsAt} size="lg" className="shadow-none border-border/50" />
        </div>

        {isLive && !isSeller ? (
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-xs font-black uppercase tracking-widest text-muted-foreground text-center">
                Ditt bud
              </p>

              {/* Amount input */}
              <div className="space-y-4">
                <div
                  className="flex items-center justify-between rounded-2xl bg-muted/30 p-2 border-2 border-border focus-within:border-accent/30 transition-all"
                >
                  <button
                    onClick={() => setAmount((v) => Math.max(suggested, v - auction.minimumIncrement))}
                    className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm border border-border text-primary hover:bg-muted active:scale-95 transition-all"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <div className="flex flex-col items-center flex-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumericInput(amount)}
                      onChange={(e) => setAmount(parseNumericInput(e.target.value))}
                      className="w-full bg-transparent text-center text-xl font-black text-primary outline-none"
                    />
                    <span className="text-[10px] font-bold text-muted-foreground">Min. {formatCurrency(suggested)}</span>
                  </div>
                  <button
                    onClick={() => setAmount((v) => v + auction.minimumIncrement)}
                    className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm border border-border text-primary hover:bg-muted active:scale-95 transition-all"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>

                {/* Quick increments */}
                <div className="grid grid-cols-3 gap-2">
                  {quickIncrements.map((inc) => (
                    <button
                      key={inc}
                      onClick={() => setAmount((auction.currentBid || auction.startingBid) + inc)}
                      className="rounded-xl py-2.5 text-xs font-bold transition-all border border-accent/20 bg-accent/5 text-accent hover:bg-accent hover:text-white"
                    >
                      +{formatCurrency(inc)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Auto-bid Management */}
            <div className="space-y-3">
              {auction.myAutoBid ? (
                <div className="rounded-2xl p-4 border border-green-600/20 bg-green-50/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-green-700">Aktivt autobud</p>
                      <p className="text-sm font-bold text-primary truncate max-w-[140px]">Maks: {formatCurrency(auction.myAutoBid.maxBidAmount)}</p>
                    </div>
                    <button
                      onClick={() => cancelAutoBid()}
                      className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:text-red-700 transition-colors"
                    >
                      Fjern
                    </button>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatNumericInput(maxBid)}
                        onChange={(e) => setMaxBid(parseNumericInput(e.target.value))}
                        className="w-full rounded-xl border border-green-600/10 bg-white px-3 py-2.5 text-xs font-bold outline-none focus:border-green-600/30"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground">kr</div>
                    </div>
                    <button
                      onClick={() => updateAutoBid(maxBid)}
                      disabled={submitting || maxBid === auction.myAutoBid.maxBidAmount}
                      className="rounded-xl bg-green-600 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white hover:bg-green-700 disabled:opacity-50 transition-all sm:min-w-[100px]"
                    >
                      Oppdater
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowAutoBid((v) => !v)}
                    className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-xs font-bold text-muted-foreground border border-dashed border-border hover:bg-muted/30 transition-all"
                  >
                    <span>Sett et autobud</span>
                    {showAutoBid ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>

                  {showAutoBid && (
                    <div className="rounded-2xl p-4 space-y-3 border border-border bg-muted/20 animate-in fade-in slide-in-from-top-2 duration-300">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                        Maksimalt autobud
                      </p>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumericInput(maxBid)}
                          onChange={(e) => setMaxBid(parseNumericInput(e.target.value))}
                          className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-bold focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all outline-none"
                        />
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">kr</div>
                      </div>
                      <p className="text-[10px] leading-relaxed text-muted-foreground font-medium">
                        Vi byr automatisk for deg med minste mulige økning, aldri mer enn ditt maksbeløp.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {error && (
              <div className="rounded-xl bg-destructive/5 p-3 border border-destructive/10">
                <p className="text-xs font-bold text-destructive text-center">
                  {error}
                </p>
              </div>
            )}

            {/* Action button */}
            <button
              onClick={() => placeBid(amount, showAutoBid ? maxBid : undefined)}
              disabled={submitting || amount < suggested}
              className="group relative w-full overflow-hidden rounded-2xl bg-accent py-4 text-base font-black text-white shadow-lg shadow-accent/25 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100"
            >
              <div className="relative z-10 flex items-center justify-center gap-2">
                <Gavel className="h-5 w-5 transition-transform group-hover:rotate-[-20deg]" />
                {submitting ? "Sender..." : showAutoBid ? "Bekreft autobud" : "Bekreft bud"}
              </div>
            </button>

            <p className="text-[10px] text-center text-muted-foreground font-medium px-4">
              Ved å bekrefte budet godtar du våre vilkår for handel.
            </p>

            <button
              className="w-full text-center text-xs font-medium text-accent hover:underline"
              onClick={() => alert("Autobud lar systemet by for navigate for deg automatisk opp til et maksimalt beløp du setter.")}
            >
              Hva er autobud?
            </button>
          </div>
        ) : isSeller && isLive ? (
          <div className="rounded-2xl bg-muted/30 p-6 border-2 border-dashed border-border text-center space-y-2">
            <p className="text-sm font-bold text-primary">Dette er din auksjon</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Som selger kan du ikke by på egne biler. Du kan følge med på budrunden og budhistorikken her.
            </p>
          </div>
        ) : null}

        {/* Generic inactive message - only show if not in post-sale states that show specific banners/actions */}
        {!isLive && !["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out", "pending_seller_decision"].includes(auction.status) && (
          <div
            className="mt-3 rounded-lg py-3 text-center text-sm font-bold bg-muted text-muted-foreground"
          >
            {auction.status === "ended" || auction.status === "closed"
              ? "Budrunden er avsluttet"
              : "Budrunden er ikke aktiv"}
          </div>
        )}

        {/* Document Downloads (Restored to BidPanel) */}
        {(auction.invoices.length > 0 || (isWinning && auction.status !== "live")) && (
          <div className="mt-6 flex flex-col gap-3 pt-6 border-t border-border">
            {isWinning && ["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"].includes(auction.status) && (
              <button
                onClick={async () => {
                  try {
                    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
                    const res = await fetch(`${api}/api/organizations/${organizationSlug}/auctions/${auction.id}/contract-download-url`, {
                      credentials: "include"
                    });
                    const data = await res.json();
                    if (data.url) window.open(data.url, "_blank");
                  } catch (err) {
                    console.error("Failed to download contract", err);
                  }
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary/90"
              >
                <FileText className="h-4 w-4" />
                Last ned kontrakt
              </button>
            )}

            {auction.invoices.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (auction.invoices.length === 1) {
                    const invId = auction.invoices[0].id;
                    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
                    fetch(`${api}/api/organizations/${organizationSlug}/accounting/records/${invId}/download-url`, { credentials: "include" })
                      .then(r => r.json())
                      .then(data => { if (data.url) window.open(data.url, "_blank"); });
                  } else {
                    onRefresh?.();
                  }
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-white border border-border px-4 py-3 text-sm font-bold text-primary shadow-sm transition-all hover:bg-muted"
              >
                <Download className="h-4 w-4" />
                Last ned faktura
              </button>
            )}
          </div>
        )}

        {/* Delivery Confirmation (buyer self-service) */}
        {isWinning && auction.status === "delivered" && (
          <div className="mt-6 flex flex-col gap-3 pt-6 border-t border-border">
            <p className="text-sm font-semibold text-foreground">Bilen er levert</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Bekreft at alt er i orden, eller meld fra om eventuelle avvik.
            </p>
            <button
              onClick={async () => {
                if (!window.confirm("Bekreft at du har mottatt kjøretøyet og at alt er i orden.")) return;
                try {
                  const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
                  await fetch(`${api}/api/auctions/${auction.id}/confirm-delivery`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ organizationSlug }),
                  });
                  onRefresh?.();
                } catch (err) {
                  console.error("Failed to confirm delivery", err);
                }
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4" />
              Bekreft mottak
            </button>
            <button
              onClick={async () => {
                if (!window.confirm("Er du sikker på at du vil registrere en reklamasjon?")) return;
                try {
                  const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
                  await fetch(`${api}/api/auctions/${auction.id}/file-complaint`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ organizationSlug }),
                  });
                  onRefresh?.();
                } catch (err) {
                  console.error("Failed to file complaint", err);
                }
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-white border border-destructive/30 px-4 py-3 text-sm font-bold text-destructive shadow-sm transition-all hover:bg-destructive/5"
            >
              <AlertTriangle className="h-4 w-4" />
              Registrer reklamasjon
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Buyer Negotiation Panel ───────────────────────────────────────────────────

function BuyerNegotiationPanel({
  auction,
  organizationSlug,
  onRefresh,
}: {
  auction: AuctionDetail;
  organizationSlug: string;
  onRefresh: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCounterOffer, setShowCounterOffer] = useState(false);
  const [counterAmount, setCounterAmount] = useState(auction.currentBid + auction.minimumIncrement);
  const [notes, setNotes] = useState("");

  const latestOffer = auction.offers[auction.offers.length - 1];
  const isSellerOfferPending = latestOffer?.offeredBy === "seller" && latestOffer?.status === "pending";

  async function handleAction(action: "accept" | "reject" | "offers", offerId?: string) {
    if (
      action === "accept"
      && !window.confirm(
        "Ved å godta motbudet inngår du en bindende avtale om kjøp av bilen etter Forhandlerbørsens vilkår. Vil du fortsette?",
      )
    ) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const url = offerId 
        ? `${api}/api/auctions/${auction.id}/offers/${offerId}/${action}` 
        : `${api}/api/auctions/${auction.id}/offers`;
      
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: action === "offers" 
          ? JSON.stringify({ amount: counterAmount, notes: notes || null, organizationSlug }) 
          : JSON.stringify({ organizationSlug }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Noe gikk galt");
      } else {
        onRefresh();
        setShowCounterOffer(false);
        setNotes("");
      }
    } catch {
      setError("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-xl shadow-primary/5 border border-primary/20 transition-all">
      <div className="bg-[#0a1e1e] px-6 py-4 flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-widest text-white" style={{ fontFamily: "var(--font-display)" }}>Forhandling</h3>
        {auction.offers.length > 0 && (
          <span className="rounded bg-white/10 px-2 py-1 text-[10px] font-bold text-white/80 border border-white/20">
            Runde {Math.ceil(auction.offers.length / 2)}
          </span>
        )}
      </div>
      
      <div className="p-6 space-y-6">
        <div className="space-y-6">
          {isSellerOfferPending ? (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/60">
                    Selgers motbud
                  </p>
                  <p className="text-4xl font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
                    {formatCurrency(latestOffer.amount)}
                  </p>
                </div>
                <TrendingUp className="h-5 w-5 text-accent opacity-50" />
              </div>

              {latestOffer.notes && (
                <div className="rounded-2xl border-2 border-dashed border-border bg-muted/20 p-5 text-center">
                   <p className="text-sm font-medium italic text-muted-foreground leading-relaxed">"{latestOffer.notes}"</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={() => handleAction("accept", latestOffer.id)}
                  disabled={submitting}
                  className="group relative flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#44a044] text-sm font-black text-white shadow-lg shadow-green-600/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                  Godta motbud
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowCounterOffer(!showCounterOffer)}
                    disabled={submitting}
                    className="flex h-12 items-center justify-center rounded-2xl border-2 border-border bg-white text-xs font-black text-primary transition-all hover:bg-muted active:scale-95 disabled:opacity-50"
                  >
                    {showCounterOffer ? "Avbryt" : "Gi nytt motbud"}
                  </button>
                  <button
                    onClick={() => handleAction("reject", latestOffer.id)}
                    disabled={submitting}
                    className="flex h-12 items-center justify-center rounded-2xl border-2 border-red-100 bg-red-50/30 text-xs font-black text-red-600 transition-all hover:bg-red-50 active:scale-95 disabled:opacity-50"
                  >
                    Avslå
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-6">
                <p className="text-sm font-black uppercase tracking-widest text-primary mb-3">Forhandling pågår</p>
                <p className="text-xs font-medium leading-relaxed text-muted-foreground/80 italic">
                  {latestOffer?.offeredBy === "buyer" 
                    ? "Ditt motbud er sendt. Vi venter på svar fra selger."
                    : "Auksjonen ble ikke solgt automatisk. Du kan gi et motbud til selger for å starte en forhandling."}
                </p>
              </div>
              {!showCounterOffer && (latestOffer?.offeredBy === "seller" || !latestOffer) && (
                <button
                  onClick={() => setShowCounterOffer(true)}
                  className="w-full h-12 rounded-2xl bg-primary text-sm font-black text-white shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                >
                  Gi motbud
                </button>
              )}
            </div>
          )}
        </div>

        {showCounterOffer && (
          <div className="space-y-5 border-t border-border pt-6 animate-in fade-in slide-in-from-top-4 duration-300">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">Ditt motbud (kr)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumericInput(counterAmount)}
                  onChange={(e) => setCounterAmount(parseNumericInput(e.target.value))}
                  className="h-12 w-full rounded-2xl border-2 border-border bg-white px-4 text-sm font-bold shadow-sm outline-none transition-all focus:border-primary/30 focus:ring-4 focus:ring-primary/5"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">Notat til selger (valgfritt)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="F.eks. inkludert omregistrering..."
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 text-sm font-medium shadow-sm outline-none transition-all focus:border-primary/30 focus:ring-4 focus:ring-primary/5 min-h-[100px] resize-none"
                />
              </div>
              <button
                onClick={() => handleAction("offers")}
                disabled={submitting || counterAmount <= auction.currentBid}
                className="w-full h-12 rounded-2xl bg-primary text-sm font-black text-white shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              >
                Send motbud
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3">
            <p className="text-xs font-bold text-destructive text-center">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Seller Decision Panel ──────────────────────────────────────────────────────

function SellerDecisionPanel({
  auction,
  organizationSlug,
  onRefresh,
}: {
  auction: AuctionDetail;
  organizationSlug: string;
  onRefresh: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCounterOffer, setShowCounterOffer] = useState(false);
  const [counterAmount, setCounterAmount] = useState(auction.sellerPayout + auction.minimumIncrement);
  const [notes, setNotes] = useState("");

  const isPendingDecision = ["pending_seller_decision", "negotiating"].includes(auction.status);

  const latestOffer = auction.offers.length > 0 ? auction.offers[auction.offers.length - 1] : null;
  const awaitingBuyerResponse = latestOffer?.status === "pending" && latestOffer.offeredBy === "seller";

  async function handleAction(action: "accept" | "reject" | "offers") {
    if (
      action === "accept"
      && !window.confirm(
        "Ved å godta budet inngår du en bindende avtale om salg av bilen etter Forhandlerbørsens vilkår. Vil du fortsette?",
      )
    ) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/organizations/${organizationSlug}/auctions/${auction.id}/${action}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: action === "offers" ? JSON.stringify({ amount: counterAmount, notes: notes || null }) : undefined,
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error ?? "Noe gikk galt");
      } else {
        onRefresh();
        setShowCounterOffer(false);
        setNotes("");
      }
    } catch {
      setError("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  }

  if (!isPendingDecision) return null;

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-lg border border-border">
      <div className="bg-primary px-6 py-4">
        <h3 className="text-sm font-black uppercase tracking-widest text-white">Selgerbeslutning</h3>
      </div>
      
      <div className="p-6 space-y-6">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground leading-none">
                {awaitingBuyerResponse ? "Ditt motbud (utbetaling)" : "Din utbetaling"}
              </span>
              <p className="text-4xl font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
                {formatCurrency(awaitingBuyerResponse && latestOffer ? latestOffer.sellerPayout : auction.sellerPayout)}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {awaitingBuyerResponse
              ? "Motbudet ditt er sendt til høyeste budgiver. Vi varsler deg så snart kjøper svarer."
              : "Budrunden er over. Du kan nå velge å selge bilen for dette beløpet, avslå det, eller komme med et motbud til høyeste budgiver."}
          </p>
        </div>

        {awaitingBuyerResponse ? (
          <div className="rounded-2xl border-2 border-dashed border-accent/30 bg-accent/5 p-5 text-center">
            <p className="text-[11px] font-black uppercase tracking-widest text-accent">Venter på kjøper</p>
            <p className="mt-1 text-xs font-medium text-muted-foreground">
              Du kan ikke godta det opprinnelige budet mens motbudet er aktivt.
            </p>
          </div>
        ) : (
        <div className="grid gap-3">
          <button
            onClick={() => handleAction("accept")}
            disabled={submitting}
            className="group relative w-full overflow-hidden rounded-xl bg-green-600 py-3.5 text-sm font-black text-white shadow-lg shadow-green-600/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
          >
            <div className="relative z-10 flex items-center justify-center gap-2">
              <Check className="h-4 w-4" />
              {submitting ? "Prosesserer..." : "Godta bud"}
            </div>
          </button>

          <p className="text-xs text-muted-foreground leading-relaxed">
            Ved å godta budet aksepterer du en bindende handel etter Forhandlerbørsens vilkår. Systemet lagrer
            aksepttidspunkt, bruker, IP-adresse og budgrunnlag som handelsbekreftelse.
          </p>

          <button
            onClick={() => setShowCounterOffer(!showCounterOffer)}
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-accent/20 bg-accent/5 py-3 text-sm font-black text-accent hover:bg-accent hover:text-white transition-all disabled:opacity-50"
          >
            <MessageSquare className="h-4 w-4" />
            {showCounterOffer ? "Avbryt motbud" : "Gi motbud"}
          </button>

          {showCounterOffer && (
            <div className="rounded-2xl bg-muted/30 p-4 border border-border space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Ditt ønskede utbetalingsbeløp (kr)</label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumericInput(counterAmount)}
                      onChange={(e) => setCounterAmount(parseNumericInput(e.target.value))}
                      className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-bold shadow-sm focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all outline-none"
                    />
                    <p className="mt-1.5 px-1 text-[9px] font-bold text-muted-foreground uppercase">
                      Dette beløpet utbetales til deg ved salg
                    </p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Notat til kjøper (valgfritt)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="F.eks. inkludert vinterhjul..."
                    className="w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-medium shadow-sm focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all outline-none min-h-[80px]"
                  />
                </div>
                <button
                  onClick={() => handleAction("offers")}
                  disabled={submitting || counterAmount <= auction.sellerPayout}
                  className="w-full rounded-xl bg-accent py-3 text-xs font-black text-white shadow-lg shadow-accent/20 hover:scale-[1.02] transition-all disabled:opacity-50"
                >
                  Send motbud
                </button>
              </div>
            </div>
          )}

          <button
            onClick={() => handleAction("reject")}
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-red-600/10 bg-red-50/50 py-3 text-sm font-black text-red-600 hover:bg-red-600 hover:text-white transition-all disabled:opacity-50"
          >
            <X className="h-4 w-4" />
            Avslå bud
          </button>
        </div>
        )}

        {error && (
          <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3">
            <p className="text-xs font-bold text-destructive text-center">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Offer History ─────────────────────────────────────────────────────────────

function OfferHistory({ offers, isSeller }: { offers: AuctionOffer[], isSeller: boolean }) {
  if (offers.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-xl bg-white border border-border">
      <div className="flex items-center justify-between px-4 py-3 border-bottom border-border bg-muted/20">
        <h3 className="text-sm font-bold text-primary" style={{ fontFamily: "var(--font-display)" }}>
          Motbud & Forhandlinger
        </h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-accent">
          {offers.length} runder
        </span>
      </div>
      <div className="divide-y divide-border">
        {[...offers].reverse().map((offer) => (
          <div key={offer.id} className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${offer.offeredBy === 'seller' ? 'bg-accent' : 'bg-primary'}`} />
                <span className={`text-[10px] font-black uppercase tracking-widest ${offer.offeredBy === 'seller' ? 'text-accent' : 'text-primary'}`}>
                  {offer.offeredBy === 'seller' ? 'Selger' : 'Kjøper'}
                </span>
              </div>
              <span className="text-[10px] font-medium text-muted-foreground">
                {new Date(offer.createdAt).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            
            <div className="flex items-end justify-between gap-4">
              <div className="space-y-1">
                {isSeller ? (
                  <p className="text-lg font-black text-primary">{formatCurrency(offer.sellerPayout)}</p>
                ) : (
                  <p className="text-lg font-black text-primary">{formatCurrency(offer.amount)}</p>
                )}
                {offer.notes && (
                  <p className="max-w-[240px] rounded-lg bg-muted/30 px-3 py-2 text-xs text-muted-foreground italic">
                    "{offer.notes}"
                  </p>
                )}
              </div>
              
              <div className="pb-1">
                <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                  offer.status === 'accepted' ? 'bg-green-100 text-green-700' :
                  offer.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-accent/10 text-accent'
                }`}>
                  {offer.status === 'accepted' ? 'Akseptert' :
                   offer.status === 'rejected' ? 'Avslått' : 'Venter'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bid History ───────────────────────────────────────────────────────────────

function BidHistory({ 
  entries, 
  isSeller,
  currentOrganizationId 
}: { 
  entries: BidHistoryEntry[]; 
  isSeller: boolean;
  currentOrganizationId?: string;
}) {
  const sorted = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="overflow-hidden rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
        <h3 className="text-sm font-bold" style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}>
          Budhistorikk
        </h3>
        <span className="text-sm font-semibold" style={{ color: "var(--muted-foreground)" }}>
          {entries.length} bud
        </span>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {sorted.length === 0 ? (
          <p className="px-4 py-3 text-sm" style={{ color: "var(--muted-foreground)" }}>
            Ingen bud ennå
          </p>
        ) : (
          sorted.map((entry, idx) => (
            <div
              key={entry.bidId}
              className={`flex items-center justify-between px-4 py-2.5 bg-white ${idx === 0 ? "animate-slide-in-top bg-green-50/50" : ""}`}
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
                  {!isSeller && entry.bidderOrganizationId === currentOrganizationId 
                    ? `Deg (${entry.bidderName})` 
                    : `Budgiver ${entry.bidderNumber}`}
                </p>
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  {new Date(entry.createdAt).toLocaleString("nb-NO", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold flex items-center justify-end gap-1" style={{ color: "var(--foreground)" }}>
                  {entry.isAutoBid && <span className="text-accent text-xs font-black">#</span>}
                  {formatCurrency(isSeller ? entry.sellerPayout : entry.amount)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function AuctionDetailPage() {
  const { organizationSlug, auctionId } = useParams<{
    organizationSlug: string;
    auctionId: string;
  }>();
  const navigate = useNavigate();
  const { auctions, applyAuctionEvent } = useAuctions();
  const { organizations } = usePlatformAuth();

  const [auction, setAuction] = useState<AuctionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [myHighestBid, setMyHighestBid] = useState<number | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<AuctionDetail["invoices"] | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const currentOrg = organizations.find((o) => o.slug === organizationSlug);

  const redirectToBudrunder = useCallback(() => {
    if (!organizationSlug) {
      navigate("/", { replace: true });
      return;
    }

    navigate(`/${organizationSlug}/budrunder`, { replace: true });
  }, [navigate, organizationSlug]);

  // Statuses where the auction is no longer live. Access is gated to the seller
  // and the winning bidder — any other buyer gets redirected out of the detail page.
  const CLOSED_STATUSES = ["pending_seller_decision", "negotiating", "no_bids", "sold", "no_sale", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"];

  const canAccessClosedAuction = useCallback((value: {
    status: string;
    sellerOrganizationId: string;
    highestBidderOrganizationId: string | null;
    currentBid: number;
  }) => {
    const isClosed = CLOSED_STATUSES.includes(value.status);
    if (!isClosed) {
      return true;
    }

    const isSeller = currentOrg?.id === value.sellerOrganizationId;
    const isWinner =
      currentOrg?.id !== undefined &&
      value.highestBidderOrganizationId === currentOrg.id &&
      value.currentBid > 0;

    return isSeller || isWinner;
  }, [currentOrg?.id]);

  const fetchMyHighestBid = useCallback(async () => {
    if (!auctionId || !organizationSlug) {
      setMyHighestBid(null);
      return;
    }

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    try {
      const bidsRes = await fetch(
        `${api}/api/organizations/${organizationSlug}/buyer-bids`,
        { credentials: "include" },
      );
      if (!bidsRes.ok) {
        return;
      }

      const bids = (await bidsRes.json()) as { id: string; myHighestBid: number }[];
      const match = bids.find((b) => b.id === auctionId);
      setMyHighestBid(match?.myHighestBid ?? null);
    } catch {
      // Ignore buyer bid refresh failures; the page can still render live auction updates.
    }
  }, [auctionId, organizationSlug]);

  const fetchAuction = useCallback(async () => {
    if (!auctionId) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    try {
      const url = new URL(`${api}/api/auctions/${auctionId}`);
      if (organizationSlug) url.searchParams.set("organizationSlug", organizationSlug);

      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) {
        redirectToBudrunder();
        return;
      }
      const data = (await res.json()) as AuctionDetail;
      if (!canAccessClosedAuction(data)) {
        redirectToBudrunder();
        return;
      }
      setAuction(data);
      setError(null);
      await fetchMyHighestBid();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt");
    } finally {
      setLoading(false);
    }
  }, [auctionId, canAccessClosedAuction, fetchMyHighestBid, organizationSlug, redirectToBudrunder]);

  useEffect(() => {
    void fetchAuction();
  }, [fetchAuction]);

  const handleAuctionEvent = useCallback(
    (event: AuctionSnapshotEvent) => {
      // 1. Update global context immediately so other components/pages are in sync
      applyAuctionEvent(event);

      setAuction((current) => {
        if (!current || current.id !== event.auctionId) return current;

        // Detect "dramatic" updates (multiple new bids in one snapshot)
        // This typically happens when a user's bid is instantly countered by an autobid.
        const newBidCount = event.bidCount - current.bidCount;

        if (newBidCount > 1) {
          // Drama time! Show intermediate state with the first new bid amount.
          const currentIds = new Set(current.bidHistory.map((b) => b.bidId));
          const newBids = event.bidHistory
            .filter((b) => !currentIds.has(b.bidId))
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

          const firstNewBid = newBids[0];
          if (firstNewBid) {
            const intermediateAuction: AuctionDetail = {
              ...current,
              currentBid: firstNewBid.amount,
              bidCount: current.bidCount + 1,
              minimumIncrement: event.minimumIncrement,
            };

            // Schedule the final update after a brief delay so the user sees their "win"
            setTimeout(() => {
              setAuction((latest) => {
                if (!latest || latest.id !== event.auctionId) return latest;
                return applyAuctionSnapshotToDetail(latest, event);
              });
              // Re-fetch full auction data for user-specific fields
              void fetchAuction();
            }, 850);

            return intermediateAuction;
          }
        }

        // Normal single-bid update
        const next = applyAuctionSnapshotToDetail(current, event);
        if (!canAccessClosedAuction(next)) {
          redirectToBudrunder();
          return current;
        }
        // Re-fetch full auction data for user-specific fields (bidHistory, isWinning, etc.)
        void fetchAuction();
        return next;
      });
    },
    [
      applyAuctionEvent,
      fetchAuction,
      canAccessClosedAuction,
      redirectToBudrunder,
    ],
  );

  // SSE for real-time bid updates with reconnection
  const connectDetailSSE = useCallback(() => {
    if (!auctionId) return;
    sseRef.current?.close();

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const sse = new EventSource(`${api}/api/auctions/${auctionId}/events`, {
      withCredentials: true,
    });
    sseRef.current = sse;

    sse.addEventListener("auction", (message) => {
      try {
        const event = JSON.parse((message as MessageEvent<string>).data) as AuctionSnapshotEvent;
        handleAuctionEvent(event);
      } catch {
        // Ignore malformed events and keep the stream open.
      }
    });

    sse.onerror = () => {
      sse.close();
      sseRef.current = null;
      setTimeout(connectDetailSSE, 3000);
    };
  }, [handleAuctionEvent, auctionId]);

  useEffect(() => {
    connectDetailSSE();
    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
  }, [connectDetailSSE]);

  // Refetch auction when tab becomes visible again
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        fetchAuction();
        if (!sseRef.current || sseRef.current.readyState === EventSource.CLOSED) {
          connectDetailSSE();
        }
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchAuction, connectDetailSSE]);

  // Similar auctions — same make, different id, live
  const similar = auctions
    .filter(
      (a) =>
        a.status === "live" &&
        a.id !== auctionId &&
        a.vehicle.make === auction?.vehicle?.make,
    )
    .slice(0, 3);

  if (loading) {
    return (
      <div className="mx-auto max-w-screen-xl px-6 py-6">
        <div className="grid grid-cols-[1fr_340px] gap-8">
          <div className="flex flex-col gap-4">
            <div className="aspect-video animate-pulse rounded-xl" style={{ background: "var(--muted)" }} />
            <div className="h-8 w-2/3 animate-pulse rounded-lg" style={{ background: "var(--muted)" }} />
          </div>
          <div className="h-80 animate-pulse rounded-xl" style={{ background: "var(--muted)" }} />
        </div>
      </div>
    );
  }

  if (error || !auction) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
          {error ?? "Auksjonen ble ikke funnet"}
        </p>
      </div>
    );
  }

  const v = auction.vehicle;
  const isSeller = currentOrg?.id === auction.sellerOrganizationId;
  const isPendingDecision = ["pending_seller_decision", "negotiating"].includes(auction.status);
  const equipmentList = Array.isArray(auction.equipment) ? (auction.equipment as string[]).filter((e) => typeof e === "string") : [];

  return (
    <div className="mx-auto max-w-screen-xl px-6 py-6">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1.5 text-sm font-medium transition-colors"
        style={{ color: "var(--muted-foreground)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--foreground)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--muted-foreground)")}
      >
        <ArrowLeft className="h-4 w-4" />
        Tilbake
      </button>

      <div className="flex flex-col lg:grid lg:grid-cols-[1fr_360px] items-start gap-6 lg:gap-10">
        {/* ── Left column ──────────────────────────────── */}
        <div className="flex w-full min-w-0 flex-col gap-6 lg:gap-8">
          {/* Status Banner */}
          <StatusBanner 
            auction={auction} 
            isSeller={isSeller} 
            organizationSlug={organizationSlug ?? ""} 
            onRefresh={() => {
              void fetchAuction();
              if (auction.invoices.length > 1) {
                setSelectedInvoices(auction.invoices);
              }
            }}
          />
          
          {/* Gallery */}
          <ImageGallery images={auction.images} title={auction.title} />

          {/* Title - Mobile: Order swap/Prominent info */}
          <div className="flex flex-col gap-2">
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight text-primary"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {auction.title}
            </h1>
            <div className="flex items-center gap-3">
              {(() => {
                const isSeller = currentOrg?.id === auction.sellerOrganizationId;
                const isWinner =
                  currentOrg?.id !== undefined &&
                  auction.highestBidderOrganizationId === currentOrg.id &&
                  auction.currentBid > 0;
                const isContractSigned = ["sold", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"].includes(auction.status);

                if (isSeller || (isWinner && isContractSigned)) {
                  return (
                    v?.registrationNumber && (
                      <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-mono font-bold text-muted-foreground border border-border">
                        {v.registrationNumber}
                      </span>
                    )
                  );
                }
                return null;
              })()}
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border">
                #{auction.referenceNumber}
              </span>
              <span className="text-sm font-medium text-muted-foreground">
                Opprettet {new Date(auction.startsAt || "").toLocaleDateString("nb-NO")}
              </span>
            </div>
          </div>

          {/* Mobile Bid Panel (shown only on mobile/tablet) */}
          <div className="lg:hidden w-full">
            {isSeller && isPendingDecision ? (
              <div className="space-y-4">
                <SellerDecisionPanel
                  auction={auction}
                  organizationSlug={organizationSlug ?? ""}
                  onRefresh={fetchAuction}
                />
                <OfferHistory offers={auction.offers} isSeller={isSeller} />
              </div>
            ) : !isSeller && isPendingDecision && auction.highestBidderOrganizationId === currentOrg?.id ? (
              <div className="space-y-4">
                <BuyerNegotiationPanel
                  auction={auction}
                  organizationSlug={organizationSlug ?? ""}
                  onRefresh={fetchAuction}
                />
                <OfferHistory offers={auction.offers} isSeller={isSeller} />
              </div>
            ) : (
              <BidPanel
                auction={auction}
                organizationSlug={organizationSlug ?? ""}
                onBidPlaced={handleAuctionEvent}
                myHighestBid={myHighestBid}
                onRefresh={() => {
                  void fetchAuction();
                  if (auction.invoices.length > 1) {
                    setSelectedInvoices(auction.invoices);
                  }
                }}
                setAuction={setAuction}
              />
            )}
          </div>

          {/* Specs */}
          <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-border">
            <h2 className="mb-6 text-xl font-bold text-primary" style={{ fontFamily: "var(--font-display)" }}>
              Spesifikasjoner
            </h2>
            <SpecGrid vehicle={v} />
            {v?.conditionSummary && (
              <div className="mt-6 rounded-xl bg-muted/50 p-4 border border-dashed border-border">
                <p className="text-sm leading-relaxed text-muted-foreground italic">
                  "{v.conditionSummary}"
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          {auction.description && (
            <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-border">
              <h2 className="mb-4 text-xl font-bold text-primary" style={{ fontFamily: "var(--font-display)" }}>
                Beskrivelse
              </h2>
              <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed">
                {auction.description.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          )}

          {/* Condition report */}
          {v?.conditionReport?.url && (
            <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-border">
              <h2 className="mb-3 text-xl font-bold text-primary" style={{ fontFamily: "var(--font-display)" }}>
                Tilstandsrapport
              </h2>
              <p className="mb-4 text-sm text-muted-foreground">
                Selger har lastet opp en tilstandsrapport for kjøretøyet.
              </p>
              <a
                href={v.conditionReport.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
              >
                <FileText className="h-4 w-4" />
                Åpne tilstandsrapport (PDF)
              </a>
            </div>
          )}

          {/* Location */}
          {(auction.pickupPostalCode || auction.pickupCity) && (
            <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-border">
              <h2 className="mb-3 text-xl font-bold text-primary" style={{ fontFamily: "var(--font-display)" }}>
                Omtrentlig plassering
              </h2>
              <div className="flex items-center gap-2 text-muted-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
                <span className="text-sm font-medium">
                  {[auction.pickupPostalCode, auction.pickupCity].filter(Boolean).join(" ")}
                </span>
              </div>
            </div>
          )}

          {/* Equipment */}
          {equipmentList.length > 0 && (
            <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm border border-border">
              <h2 className="mb-6 text-xl font-bold text-primary" style={{ fontFamily: "var(--font-display)" }}>
                Utstyrsliste
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                {equipmentList.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-primary font-medium">
                    <div className="h-1.5 w-1.5 rounded-full bg-accent" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Similar auctions */}
          {similar.length > 0 && (
            <div className="mt-4">
              <h2 className="mb-6 text-2xl font-black text-primary" style={{ fontFamily: "var(--font-display)" }}>
                Liknende auksjoner
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {similar.map((a) => (
                  <BidAuctionCard
                    key={a.id}
                    auction={a}
                    organizationSlug={organizationSlug ?? ""}
                    onBidPlaced={applyAuctionEvent}
                    onToggleFavorite={() => { }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Mobile Bid History */}
          {!isSeller && (
            <div className="lg:hidden mt-4">
              <BidHistory entries={auction.bidHistory} isSeller={isSeller} currentOrganizationId={currentOrg?.id} />
            </div>
          )}
        </div>

        {/* ── Right column (sticky) ─────────────────────── */}
        <div className="hidden lg:flex flex-col gap-6 sticky top-24 w-full">
          {isSeller && isPendingDecision ? (
            <>
              <SellerDecisionPanel
                auction={auction}
                organizationSlug={organizationSlug ?? ""}
                onRefresh={fetchAuction}
              />
              <OfferHistory offers={auction.offers} isSeller={isSeller} />
            </>
          ) : !isSeller && isPendingDecision && auction.highestBidderOrganizationId === currentOrg?.id ? (
            <>
              <BuyerNegotiationPanel
                auction={auction}
                organizationSlug={organizationSlug ?? ""}
                onRefresh={fetchAuction}
              />
              <OfferHistory offers={auction.offers} isSeller={isSeller} />
            </>
          ) : (
            <BidPanel
              auction={auction}
              organizationSlug={organizationSlug ?? ""}
              onBidPlaced={handleAuctionEvent}
              myHighestBid={myHighestBid}
              onRefresh={() => {
                void fetchAuction();
                if (auction.invoices.length > 1) {
                  setSelectedInvoices(auction.invoices);
                }
              }}
              setAuction={setAuction}
            />
          )}
          {!isSeller && (
            <BidHistory entries={auction.bidHistory} isSeller={isSeller} currentOrganizationId={currentOrg?.id} />
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedInvoices && (
        <InvoicesModal
          invoices={selectedInvoices}
          organizationSlug={organizationSlug ?? ""}
          onClose={() => setSelectedInvoices(null)}
        />
      )}
    </div>
  );
}
